import type { WholeStoryType } from "@config";
import { environment } from "@environment";
import { useCallback, useMemo } from "react";
import { useGetUploadFileTokenMutation } from "@/shared/services";
import { renderStory, uploadFile } from "../rendering-provider/utils";

export const useGenerateCanonical = () => {
  const {
    mutateAsync: getUploadFileToken,
    isPending: isLoading,
    isError: error,
  } = useGetUploadFileTokenMutation();

  const handleCanonical = useCallback(
    async (
      story: WholeStoryType,
      canonical: string,
      options: { signal?: AbortSignal } = {},
    ) => {
      try {
        // Generate upload token
        const result = await getUploadFileToken({
          filename: `${story.orgId}/${canonical}`,
          contentType: "text/html",
          typeStory: true,
        });

        // Pre-compute the final URL
        const url = `${environment.stories}/${result.url.fields.key}`;

        // Render story and create blob in parallel with upload preparation
        const doc = renderStory({ story, preview: false });
        const blob = new Blob([doc], { type: "text/html" });

        // Upload file. `uploadFile` hands back the raw Response and does NOT
        // check it, so a rejected signed POST (GCS 4xx/5xx) used to resolve
        // like a success and this function returned a URL for an object that
        // was never written. Publish then went live against a stale template,
        // and useVersionTemplateCanonical handed the backend a
        // duplicateFromCanonicalId whose object does not exist — getStoryTemplate
        // throws there AFTER the version row is inserted, so the create 500s
        // and leaves an orphan version. Fail here instead: both callers already
        // treat a throw as "fall back to the published template".
        const response = await uploadFile(result.url, blob, {
          signal: options.signal,
        });
        if (!response.ok)
          throw new Error(
            `canonical upload rejected with ${response.status} for ${story.orgId}/${canonical}`,
          );

        return url;
      } catch (err) {
        console.error("Failed to generate canonical:", err);
        throw err;
      }
    },
    [getUploadFileToken],
  );

  // Memoize the return value to prevent unnecessary re-renders
  return useMemo(
    () => ({
      generateCanonical: handleCanonical,
      isLoading,
      error,
    }),
    [handleCanonical, isLoading, error],
  );
};
